<template>
  <div class="grow">
    <template v-if="inputType === 'switch'">
      <v-switch
        v-model="computedValue"
        :label="label"
        :hint="tooltip"
        persistent-hint
        dense
      ></v-switch>
    </template>
    <template v-else-if="inputType === 'slider'">
      <v-slider
        v-model="computedValue"
        :label="`${label}: 1e${computedValue}`"
        :hint="tooltip"
        :max="max"
        :min="min"
      />
    </template>
    <template v-else-if="inputType === 'dual-constant-profile'">
      <InputDualParam
        :label="label"
        :auto-allocation="autoAllocation"
        :auto-allocated.sync="autoAllocated"
        :tooltip="tooltip"
      >
        <template #constant-switch>
          <v-switch v-model="constantSwitch" class="mt-0" dense>
            <template #label>
              <p class="my-auto text-caption">Constant Values</p>
            </template>
          </v-switch>
        </template>
        <template #default>
          <div class="pt-8 pr-8 max-width-600">
            <InputServiceParam
              v-model="computedValue"
              :input-type="constantSwitch ? 'inputYearly' : 'inputYearlyProfile'"
              :num_years="num_years"
              :suffix="suffix"
              :year_start="year_start"
              :multi-year-upload="true"
              :spread-values="true"
              :fill-value="fillValue"
              :profile-type="profileType"
              hide-label
            />
          </div>
        </template>
      </InputDualParam>
    </template>
    <template v-else-if="inputType === 'profile'">
      <InputProfile :label="label"></InputProfile>
    </template>
    <template v-else-if="inputType === 'select'">
      <v-select v-model="computedValue" :label="label" :hint="tooltip" :items="items" />
    </template>
    <template v-else-if="inputType === 'inputYearly'">
      <InputYearlyProfile
        v-model="computedValue"
        :label="label"
        :num_years="num_years"
        :year_start="year_start"
        fill
        :suffix="suffix"
        :fill-value="fillValue"
        object-value
        items-type="constant"
        spread-values
      />
    </template>
    <template v-else-if="inputType === 'inputYearlyProfile'">
      <InputYearlyProfile
        v-model="computedValue"
        :label="label"
        :suffix="suffix"
        :hint="tooltip"
        :num_years="num_years"
        :profile-type="profileType"
        :multi-year-upload="true"
        :hide-label="hideLabel"
        items-type="profile"
        object-value
      />
    </template>
    <template v-else-if="inputType === 'numberTextField'">
      <InputTextField
        v-model="computedValue"
        :label="label"
        :hint="tooltip"
        :suffix="suffix"
        type="number"
        :nullable="nullable"
      />
    </template>
    <template v-else-if="inputType === 'inputPeakHours'">
      <InputServicePeakHours
        v-model="computedValue"
        :label="label"
        :tooltip="tooltip"
        :suffix="suffix"
        :year_start="year_start"
        :num_years="num_years"
      />
    </template>
    <template v-if="inputType === 'custom'">
      <slot name="custom"></slot>
    </template>
  </div>
</template>

<script>
export default {
  name: "InputServiceParam",
  components: {
    InputDualParam: () => import("@/components/InputDualParam.vue"),
    InputTextField: () => import("@/components/inputs/InputTextField.vue"),
    InputYearlyProfile: () => import("@/components/InputYearlyProfile.vue"),
    InputServicePeakHours: () => import("@/components/InputServicePeakHours.vue"),
  },
  mixins: [],
  props: {
    value: { required: true },
    year_start: { type: Number, required: true },
    num_years: { type: Number, required: true },
    label: { type: String, required: false, default: "" },
    inputType: {
      type: String,
      required: true,
      validator: (v) =>
        [
          "switch",
          "dual-constant-profile",
          "inputYearly",
          "inputYearlyProfile",
          "inputPeakHours",
          "numberTextField",
          "profile",
          "select",
          "slider",
          "custom",
          "aggregator",
        ].includes(v),
    },
    fillValue: { type: Number, required: false, default: -1 },
    suffix: { type: String, required: false, default: "" },
    tooltip: { type: String, required: false, default: "" },
    items: { type: Array, required: false, default: () => [] },
    yearly: { type: Boolean, required: false, default: false },
    autoAllocation: { type: Boolean, required: false, default: false },
    profileType: { type: String, required: false, default: "" },
    hideLabel: { type: Boolean, required: false, defalut: false },
    multiYearUpload: { type: Boolean, required: false, default: false },
    nullable: { type: Boolean, required: false, default: false },
    max: { type: [Number, String], required: false, default: null },
    min: { type: [Number, String], required: false, default: null },
  },
  data() {
    return {
      constantSwitch: true,
      constantSwitchMounted: false,
    };
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.$emit("change");
      },
    },
    autoAllocated: {
      get() {
        if (this.autoAllocation) {
          return Boolean(
            this.value &&
              this.value.length === this.num_years &&
              this.value.every((v) => v.value === -1)
          );
        }
        return false;
      },
      set(val) {
        const allocatedValue = Array(this.num_years).fill({
          _resource: "CONSTANT",
          value: val ? -1 : 0,
        });
        this.$emit("input", allocatedValue);
        this.$emit("change");
      },
    },
  },
  watch: {
    constantSwitch(val) {
      if (this.constantSwitchMounted) {
        const resetValue = Array(this.num_years).fill(
          val ? { _resource: "CONSTANT", value: 0 } : { _resource: "PROFILE", id: null }
        );
        this.$emit("input", resetValue);
        this.$emit("change");
      }
      this.constantSwitchMounted = true;
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {
    if (this.inputType === "dual-constant-profile" && this.value) {
      // eslint-disable-next-line no-underscore-dangle
      this.constantSwitch = Boolean(this.value[0]._resource === "CONSTANT");
    }
  },
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {},
};
</script>

<style>
.max-width-600 {
  max-width: 600px;
}
</style>
